import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { Row, Col } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { initialQuotationValues, QuotationFormValues } from "./initialValues";
import QuotationDetailsSection from "./Sections/QuotationDetailsSection";
import CustomerDetailsSection from "./Sections/CustomerDetailsSection";
import BillingFromAddress from "Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";
import { DEFAULT_ORGANISATION } from "./serviceInitialValues";
import { MecordColors } from "Constants/Color";
import AgentDetailsSection from "./Sections/AgentDetailsSection";
import AssignMembersSection from "./Sections/AssignMembersSection";
import CurrencyExchangeSection from "./Sections/CurrencyExchangeSection";
import EMDSection from "./Sections/EMDSection";
import InspectionInstallationSection from "./Sections/InspectionInstallationSection";
import ProductListSection from "./Sections/ProductListSection";
import TermsConditionsSection from "./Sections/TermsConditionsSection";
import NotesAttachmentsSection from "./Sections/NotesAttachmentsSection";
import FollowUpSection from "./Sections/FollowUpSection";
import FormActionButtons from "./Components/FormActionButtons";
import AddTagsForQuotation from "./Sections/AddTagsForQuotation";
import { ArrowBack } from "@mui/icons-material";
import { format } from "date-fns";
import {
  createProductQuotation,
  fetchProductQuotationById,
  updateProductQuotation,
} from "slices/quotation";
import { fetchProductEnquiryById } from "slices/enquiry/thunk";
import { mapEnquiryToQuotationForm } from "./utils/mapEnquiryToQuotationForm";
import * as Yup from "yup";
import { clearAllTableStates } from "utils/clearTableStates";
import LoadingSpinner from "components/LoadingSpinner";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import type { FormikErrors } from "formik";

function ScrollToFormikErrorWrapper({
  errors,
  submitCount,
  children,
}: {
  errors: FormikErrors<any>;
  submitCount: number;
  children: React.ReactNode;
}) {
  useScrollToFormikError(errors, submitCount);
  return <>{children}</>;
}

// Simple UUID generation function
const generateUUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Transform backend quotation data to form format
const transformQuotationDataToForm = (
  quotationData: any
): QuotationFormValues => {
  const {
    quotation_product_id,
    quotation_number,
    quotation_date,
    valid_until_date,
    initiated_by,
    enquiry_product_id,
    enquiry_number,
    reference_number,
    quotation_stage,
    customer_details,
    customer_name,
    agent_name,
    quotation_status,
    currency_name,
    currency_code,
    currency_symbol,
    currency_rate_in_inr,
    mode_of_emd,
    emd_amount,
    inspection,
    inspection_date,
    installation,
    installation_date,
    notes,
    attachments,
    terms_and_conditions,
    quotation_product_items,
    assigned_members,
    follow_up_date,
  } = quotationData;

  // Transform customer details
  const company = customer_details
    ? {
        company_id: customer_details.company_id || customer_details.customer_id,
        company_name:
          customer_details.company_name || customer_details.customer_name,
        company_email: customer_details.company_email || customer_details.email,
        vendor_code: customer_details.vendor_code || customer_details.vendor_code,
        branch_name:
          customer_details.branch_name || customer_details.customer_branch_name,
        phone_number: customer_details.phone_number,
        gstin: customer_details.gstin,
        gst_type: customer_details.gst_type || customer_details.type,
        industry: customer_details.industry,
        billing_address: customer_details.billing_address,
        shipping_addresses:
          customer_details.shipping_addresses ||
          customer_details.shipping_address ||
          [],
        contacts:
          customer_details.contact_details || customer_details.contacts || [], // Include contacts for the component to work properly
      }
    : null;

  // Transform contact persons
  const contact_persons =
    (customer_details?.contact_details || customer_details?.contacts)?.map(
      (contact: any) => ({
        id: contact.contact_id || contact.id,
        name: contact.name || contact.contact_name,
        designation:
          contact.designation || contact.contact_person_designation || "",
        phone_country:
          contact.phone_country || contact.contact_person_phone_country || "",
        phone_number: contact.phone_number || contact.contact_person_phone_no,
        email: contact.email || contact.contact_person_email,
      })
    ) || [];

  // Transform assigned members
  const members =
    assigned_members?.map((member: any) => {
      const firstName = member.first_name || "";
      const lastName = member.last_name || "";
      const initials = (
        (firstName.charAt(0) || "") + (lastName.charAt(0) || "")
      ).toUpperCase();
      return {
        id: member.user_id,
        name:
          member.username ||
          `${firstName} ${lastName}`.trim() ||
          member.first_name,
        first_name: firstName,
        last_name: lastName,
        email: member.email,
        initials:
          initials ||
          (member.username
            ? member.username.substring(0, 2).toUpperCase()
            : ""),
      };
    }) || [];

  // Transform products
  const products =
    quotation_product_items?.map((item: any, index: number) => {
      // Validate that product_id is a valid format (at least not a timestamp)
      const isValidProductId =
        item.product_id &&
        item.product_id.length > 10 &&
        !/^\d+$/.test(item.product_id);

      if (!isValidProductId && item.product_id) {
        console.warn(
          `Invalid product_id detected: ${item.product_id} for product ${item.product_name}. Using generated UUID.`
        );
      }

      // Extract and normalize shipping address object
      let shippingAddressObject = null;

      if (item.shipping_address) {
        // Handle array format (backend returns array)
        if (
          Array.isArray(item.shipping_address) &&
          item.shipping_address.length > 0
        ) {
          const firstAddress = item.shipping_address[0];
          // Normalize the address object - ensure consistent field names
          shippingAddressObject = {
            shipping_address_id:
              firstAddress.shipping_address_id || firstAddress.id,
            company_id: firstAddress.company_id,
            address: firstAddress.address,
            city: firstAddress.city,
            state: firstAddress.state,
            country: firstAddress.country,
            pincode: firstAddress.pincode || firstAddress.pin_code,
            phone_number: firstAddress.phone_number,
            phone_country: firstAddress.phone_country,
            is_default: firstAddress.is_default || false,
            created_at: firstAddress.created_at,
            updated_at: firstAddress.updated_at,
          };
        }
        // Handle object format
        else if (typeof item.shipping_address === "object") {
          // Normalize the address object - ensure consistent field names
          shippingAddressObject = {
            shipping_address_id:
              item.shipping_address.shipping_address_id ||
              item.shipping_address.id,
            company_id: item.shipping_address.company_id,
            address: item.shipping_address.address,
            city: item.shipping_address.city,
            state: item.shipping_address.state,
            country: item.shipping_address.country,
            pincode:
              item.shipping_address.pincode || item.shipping_address.pin_code,
            phone_number: item.shipping_address.phone_number,
            phone_country: item.shipping_address.phone_country,
            is_default: item.shipping_address.is_default || false,
            created_at: item.shipping_address.created_at,
            updated_at: item.shipping_address.updated_at,
          };
        }
        // Handle string format (legacy - should not happen, but handle for backward compatibility)
        else if (typeof item.shipping_address === "string") {
          // Keep as null - will need to be selected from dropdown
          shippingAddressObject = null;
        }
      }

      return {
        id: isValidProductId ? item.product_id : generateUUID(),
        sr_no: index + 1,
        product_name: item.product_name,
        sku: item.product_sku,
        hsn_code: item.hsn_code,
        make: item.make_name,
        model: item.model_name,
        is_serviceable: item.serviceable,
        quantity: item.product_quantity,
        unit: item.unit_name,
        unit_price: parseFloat(item.unit_price),
        discount_type: item.discount_type || "currency",
        discount_value: parseFloat(item.discount_value) || 0, // raw value from backend
        calculated_discount_value: 0, // will be recalculated by useEffect
        net_value: parseFloat(item.product_net_total) || 0,
        gst_percentage: parseFloat(item.gst_percentage) || 0,
        gst_value: parseFloat(item.gst_value) || 0,
        total: parseFloat(item.product_total) || 0,
        shipping_address: shippingAddressObject, // Always store as full object (null if not available)
        rate_in_currency: parseFloat(item.rate_in_currency) || 0,
        gross_value: parseFloat(item.gross_value) || 0,
        // Additional fields for form compatibility
        category_name: item.category_name,
        sub_category_name: item.sub_category,
        type: item.product_type,
        remark: item.remark,
        serial_number: item.serial_number,
        prod_description: item.prod_description ?? item.description ?? "",
        additional_details: item.additional_details ?? "",
      };
    }) || [];

  // Transform terms and conditions - group by type to handle multiple options per type
  console.log("terms_and_conditions from backend:", terms_and_conditions);
  let transformedTermsAndConditions: any[] = [];
  if (Array.isArray(terms_and_conditions) && terms_and_conditions.length > 0) {
    // Group terms by type
    const groupedByType: Record<string, string[]> = {};
    terms_and_conditions.forEach((term: any) => {
      if (term.type && term.options) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        // Add option if not already present
        if (!groupedByType[term.type].includes(term.options)) {
          groupedByType[term.type].push(term.options);
        }
      }
    });

    // Convert grouped data to form format
    transformedTermsAndConditions = Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "", // First option for backward compatibility
        description: options.join("\n"), // All options as newline-separated
      })
    );
  } else if (terms_and_conditions && typeof terms_and_conditions === "object") {
    // Handle case where it's an object instead of array
    const termsArray = Object.values(terms_and_conditions);
    const groupedByType: Record<string, string[]> = {};
    termsArray.forEach((term: any) => {
      if (term.type && term.options) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        if (!groupedByType[term.type].includes(term.options)) {
          groupedByType[term.type].push(term.options);
        }
      }
    });

    transformedTermsAndConditions = Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "",
        description: options.join("\n"),
      })
    );
  }
  console.log("transformedTermsAndConditions:", transformedTermsAndConditions);

  // Helper function to convert UTC date to local date string (YYYY-MM-DD format)
  // Backend returns UTC dates, we need to convert to local for display
  const formatDateToDisplay = (utcDateStr: string): string => {
    if (!utcDateStr) return "";
    try {
      // Parse UTC date string
      const utcDate = new Date(utcDateStr);
      if (isNaN(utcDate.getTime())) return "";
      
      // Get local date components
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, "0");
      const day = String(utcDate.getDate()).padStart(2, "0");
      
      // Return in YYYY-MM-DD format (for HTML date input)
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  return {
    quotation_date: quotation_date ? formatDateToDisplay(quotation_date) : "",
    valid_until: valid_until_date ? formatDateToDisplay(valid_until_date) : "",
    enquiry_reference_number: enquiry_number || "",
    enquiry_product_id: enquiry_product_id || "",
    reference_number: reference_number || "",
    initiated_by: initiated_by || "",
    company,
    contact_persons,
    agent_name: agent_name || "",
    members,
    currency: currency_code || currency_name || "INR",
    currency_symbol: currency_symbol || "₹",
    exchange_rate: currency_rate_in_inr?.toString() || "1",
    mode_of_emd: mode_of_emd || "",
    emd_amount: emd_amount?.toString() || "",
    inspection: inspection || "",
    inspection_date: inspection_date
      ? formatDateToDisplay(inspection_date)
      : "",
    installation: installation || "",
    installation_date: installation_date
      ? formatDateToDisplay(installation_date)
      : "",
    products,
    terms_and_conditions: transformedTermsAndConditions,
    notes: notes || "",
    attachments: attachments || [],
    next_follow_up_date: follow_up_date
      ? formatDateToDisplay(follow_up_date)
      : "",
    quotation_id: quotation_product_id,
    quotation_status: quotation_status || undefined,
    quotation_stage: quotation_stage || undefined,
    customer_details,
    tags: quotationData.tags || [],
    bill_from_address: quotationData.bill_from || DEFAULT_ORGANISATION,
  } as QuotationFormValues;
};

// Function to create validation schema based on edit mode
const createValidationSchema = (isEditMode: boolean) => {
  const baseSchema = {
    quotation_date: Yup.string().required("Quotation date is required"),
    valid_until: Yup.string().required("Valid until date is required"),
    enquiry_reference_number: Yup.string(),
    initiated_by: Yup.string().trim().required("Initiated by is required"),
    company: Yup.object().nullable().required("Company selection is required"),
    contact_persons: Yup.array().min(
      1,
      "At least one contact person is required"
    ),
    members: Yup.array(),
    currency: Yup.string().required("Currency is required"),
    exchange_rate: Yup.string().required("Exchange rate is required"),
    products: Yup.array()
      .min(1, "At least one product is required")
      .of(
        Yup.object().shape({
          quantity: Yup.number()
            .min(1, "Quantity must be greater than 0")
            .required("Quantity is required"),
          shipping_address: Yup.mixed()
            .required("Shipping address is required for all products")
            .test(
              "is-valid-shipping-address",
              "Complete shipping address is required",
              (value: any) => {
                return (
                  value &&
                  typeof value === "object" &&
                  value.shipping_address_id &&
                  value.address &&
                  value.city &&
                  value.state &&
                  value.pincode
                );
              }
            ),
        })
      ),
    terms_and_conditions: Yup.array(),
    inspection_date: Yup.string().when(["inspection"], ([inspection], schema) =>
      inspection === "Applicable"
        ? schema.required(
            "Inspection date is required when inspection is applicable"
          )
        : schema
    ),
    installation_date: Yup.string().when(
      ["installation"],
      ([installation], schema) =>
        installation === "Applicable"
          ? schema.required(
              "Installation date is required when installation is applicable"
            )
          : schema
    ),
    emd_amount: Yup.string().when(["mode_of_emd"], ([mode_of_emd], schema) =>
      mode_of_emd && mode_of_emd.length > 0
        ? schema
            .required("EMD Amount is required when EMD mode is selected")
            .test(
              "is-valid-amount",
              "EMD Amount must be greater than 0",
              (value: any) => {
                return value && parseFloat(value) > 0;
              }
            )
        : schema
    ),
  };

  // Only add follow-up validation in create mode
  if (!isEditMode) {
    (baseSchema as any).next_follow_up_date = Yup.string().required(
      "Next follow up date is required"
    );
  }

  return Yup.object().shape(baseSchema);
};

const ProductQuotationForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id, enquiryId } = useParams<{ id?: string; enquiryId?: string }>();
  const isEditMode = Boolean(id);
  const isCreateFromEnquiry = Boolean(enquiryId);

  const [initialValues, setInitialValues] = useState<QuotationFormValues>(
    initialQuotationValues
  );
  const [isLoading, setIsLoading] = useState(isEditMode || isCreateFromEnquiry);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchQuotationData = async () => {
        try {
          setIsLoading(true);
          const result = await dispatch(fetchProductQuotationById(id) as any);

          if (fetchProductQuotationById.fulfilled.match(result)) {
            const quotationData = result.payload;
            console.log("quotationData =>", quotationData);
            // Transform backend data to form format
            const transformedData = transformQuotationDataToForm(quotationData);
            // console.log("transformedData =>", transformedData);
            // console.log("transformedData.company =>", transformedData.company);
            // console.log("transformedData.company.contacts =>", transformedData.company?.contacts);
            // console.log("transformedData.contact_persons =>", transformedData.contact_persons);
            // console.log("transformedData.customer_details =>", transformedData.customer_details);
            // console.log("transformedData.quotation_date =>", transformedData.quotation_date);
            // console.log("transformedData.valid_until =>", transformedData.valid_until);
            // console.log("transformedData.inspection_date =>", transformedData.inspection_date);
            // console.log("transformedData.installation_date =>", transformedData.installation_date);
            // console.log("original customer_details.contact_details =>", quotationData.customer_details?.contact_details);

            setInitialValues(transformedData);
          } else {
            toast.error("Failed to load quotation data");
            navigate("/quotation/product");
          }
        } catch (error) {
          console.error("Error fetching quotation:", error);
          toast.error("An error occurred while loading the quotation");
          navigate("/quotation/product");
        } finally {
          setIsLoading(false);
        }
      };

      fetchQuotationData();
    }
  }, [id, isEditMode, dispatch, navigate]);

  useEffect(() => {
    if (!isCreateFromEnquiry || !enquiryId || isEditMode) return;

    const loadEnquiryForQuotation = async () => {
      try {
        setIsLoading(true);
        const result = await dispatch(fetchProductEnquiryById(enquiryId) as any);
        if (fetchProductEnquiryById.fulfilled.match(result)) {
          const enquiryData = result.payload?.data || result.payload;
          const mapped = mapEnquiryToQuotationForm(enquiryData, "product");
          setInitialValues((prev) => ({
            ...prev,
            ...mapped,
          }));
        } else {
          toast.error("Failed to load enquiry data");
          navigate("/quotation/product/add");
        }
      } catch (error) {
        console.error("Error loading enquiry for quotation:", error);
        toast.error("An error occurred while loading the enquiry");
        navigate("/quotation/product/add");
      } finally {
        setIsLoading(false);
      }
    };

    loadEnquiryForQuotation();
  }, [enquiryId, isCreateFromEnquiry, isEditMode, dispatch, navigate]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSaveDraft = async (values: QuotationFormValues) => {
    setIsSavingDraft(true);
    try {
      if (isEditMode && id) {
        // Update existing quotation - preserve existing quotation_status
        const result = await dispatch(
          updateProductQuotation({
            id,
            ...values,
            quotationStatus: values.quotation_status || undefined,
            quotationStage: "Draft",
          }) as any
        );

        if (updateProductQuotation.fulfilled.match(result)) {
          toast.success("Product quotation updated as draft successfully!");
          navigate("/quotation/product");
        } else {
          console.error("Failed to update draft:", result.payload);
          toast.error(
            result.payload?.message ||
              "Failed to update draft. Please try again."
          );
        }
      } else {
        // Create new quotation
        const result = await dispatch(
          createProductQuotation({
            formData: values,
            quotationStatus: "Prepared",
            quotationStage: "Draft",
          }) as any
        );

        if (createProductQuotation.fulfilled.match(result)) {
          toast.success("Product quotation saved as draft successfully!");
          clearAllTableStates();
          navigate("/quotation/product");
        } else {
          console.error("Failed to save draft:", result.payload);
          toast.error(
            result.payload?.message || "Failed to save draft. Please try again."
          );
        }
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSave = async (values: QuotationFormValues) => {
    setIsSaving(true);
    try {
      console.log("Save:", values);

      if (isEditMode && id) {
        // Update existing quotation - preserve existing quotation_status
        const result = await dispatch(
          updateProductQuotation({
            id,
            ...values,
            quotationStatus: values.quotation_status || undefined,
            quotationStage: "Log",
          }) as any
        );

        if (updateProductQuotation.fulfilled.match(result)) {
          toast.success("Product quotation updated successfully!");
          navigate("/quotation/product");
        } else {
          console.error("Failed to update quotation:", result.payload);
          toast.error(
            result.payload?.message ||
              "Failed to update quotation. Please try again."
          );
        }
      } else {
        // Create new quotation
        const result = await dispatch(
          createProductQuotation({
            formData: values,
            quotationStatus: "Prepared",
            quotationStage: "Log",
          }) as any
        );

        if (createProductQuotation.fulfilled.match(result)) {
          toast.success("Product quotation created successfully!");
          clearAllTableStates();
          navigate("/quotation/product");
        } else {
          console.error("Failed to create quotation:", result.payload);
          toast.error(
            result.payload?.message ||
              "Failed to create quotation. Please try again."
          );
        }
      }
    } catch (error: any) {
      console.error("Error creating quotation:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="d-flex align-items-center">
          <ArrowBack
            className="me-2"
            style={{ fontSize: "1.25rem", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h5
            className="mb-0 fw-bold"
            style={{ fontFamily: "Poppins, sans-serif", fontSize: "17px" }}
          >
            <Link to={""} className="text-decoration-none text-dark">
              {isEditMode
                ? "Edit Product Quotation"
                : "Create Product Quotation"}
            </Link>
          </h5>
        </div>
        <LoadingSpinner label="Loading quotation..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* <PageBreadcrumb
        title={isEditMode ? "Edit Product Quotation" : "Create Product Quotation"}
        breadcrumbs={[
          { label: "Quotation", to: "/quotation" },
          { label: isEditMode ? "Edit Product Quotation" : "Create Product Quotation", to: isEditMode ? `/quotation/product/edit/${id}` : "/quotation/add-product-quotation" },
        ]}
      /> */}

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={createValidationSchema(isEditMode)}
        onSubmit={handleSave}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          validateForm,
          setTouched,
          errors,
          submitCount,
          touched,
          setFieldTouched,
        }) => {
          const isBasicDetailsLocked = Boolean(
            (values as any).has_order || (values as any).sales_order_product_id
          );
          return (
            <ScrollToFormikErrorWrapper errors={errors} submitCount={submitCount}>
            <>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
              {/* Left: Back arrow + Title */}
              <div className="d-flex align-items-center">
                <ArrowBack
                  className="me-2"
                  style={{ fontSize: "1.25rem", cursor: "pointer" }}
                  onClick={() => navigate(-1)}
                />
                <h5
                  className="mb-0 fw-bold"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "17px",
                  }}
                >
                  <Link to={""} className="text-decoration-none text-dark">
                    {isEditMode
                      ? "Edit Sales Quotation"
                      : "Create Sales Quotation"}
                  </Link>
                </h5>
              </div>

              {/* Top Action Buttons */}
              <FormActionButtons
                onCancel={handleCancel}
                showSaveDraft={!isEditMode}
                onSaveDraft={async () => {
                  // Mark all fields as touched to show validation errors
                  const fields = Object.keys(values);
                  const touchedFields: any = {};
                  fields.forEach((field) => {
                    touchedFields[field] = true;
                  });
                  setTouched(touchedFields);

                  // Validate form
                  const errors = await validateForm();

                  // If there are validation errors, don't save
                  if (Object.keys(errors).length > 0) {
                    toast.error(
                      "Please fix validation errors before saving as draft"
                    );
                    return;
                  }

                  // If validation passes, save as draft
                  handleSaveDraft(values as QuotationFormValues);
                }}
                onSave={async () => {
                  // Mark all fields as touched to show validation errors
                  const fields = Object.keys(values);
                  const touchedFields: any = {};
                  fields.forEach((field) => {
                    touchedFields[field] = true;
                  });
                  setTouched(touchedFields);

                  // Validate form
                  const errors = await validateForm();

                  // If there are validation errors, don't save
                  // if (Object.keys(errors).length > 0) {
                  //   toast.error(
                  //     "Please fix validation errors before saving the quotation"
                  //   );
                  //   return;
                  // }

                  // If validation passes, submit the form
                  handleSubmit();
                }}
                isSaving={isSaving}
                isSavingDraft={isSavingDraft}
              />
            </div>

            <hr />

            <Form>
              {/* Quotation Details Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <QuotationDetailsSection />
                </Col>

                {/* Customer Details Section */}
                <Col xs={12} md={6}>
                  <CustomerDetailsSection />
                </Col>
              </Row>

              <hr />

              {/* Billing Details Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <h5 className="fw-semibold mb-3">Billing Details</h5>
                  <div
                    className="p-3 rounded mb-3"
                    style={{ backgroundColor: MecordColors?.LightGrayColor }}
                  >
                    <BillingFromAddress
                      value={values.bill_from_address}
                      disabled={isBasicDetailsLocked}
                      onChange={async (newValue) => {
                        await setFieldValue("bill_from_address", newValue);
                        setFieldTouched("bill_from_address", true);
                      }}
                      onBlur={async () => {
                        setFieldTouched("bill_from_address", true);
                      }}
                      error={touched.bill_from_address ? (errors.bill_from_address as string) : undefined}
                    />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Agent Details & Assign Members Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <AgentDetailsSection />
                </Col>
                <Col xs={12} md={6}>
                  <AssignMembersSection />
                </Col>
              </Row>
              <Row className="mb-5">
                <Col xs={12} md={6}>
                  <AddTagsForQuotation
                    value={values.tags || []}
                    onChange={(tags) => setFieldValue("tags", tags)}
                  />
                </Col>
              </Row>

              <hr />

              {/* Currency & Exchange & EMD Details Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <CurrencyExchangeSection />
                </Col>
                <Col xs={12} md={6}>
                  <EMDSection />
                </Col>
              </Row>

              <hr />

              {/* Inspection & Installation Section */}
              <Row className="mb-3">
                <Col md={12}>
                  <InspectionInstallationSection />
                </Col>
              </Row>

              <hr />

              {/* Product List Section - id for useScrollToFormikError when "at least one product" validation fails */}
              <Row className="mb-3" id="products">
                <Col md={12} id="products">
                  <ProductListSection />
                </Col>
              </Row>

              <hr />

              {/* Terms & Conditions Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <TermsConditionsSection />
                </Col>

                <Col xs={12} md={6}>
                  {/* Notes & Attachments Section */}
                  <NotesAttachmentsSection />

                  <hr />
                  {/* Follow-up Section - only show in create mode */}
                  {!isEditMode && <FollowUpSection />}
                </Col>
              </Row>

              <hr />

              {/* Bottom Action Buttons */}
              <Row className="mt-4">
                <Col xs={12}>
                  <FormActionButtons
                    onCancel={handleCancel}
                    showSaveDraft={!isEditMode}
                    onSaveDraft={async () => {
                      // Mark all fields as touched to show validation errors
                      const fields = Object.keys(values);
                      const touchedFields: any = {};
                      fields.forEach((field) => {
                        touchedFields[field] = true;
                      });
                      setTouched(touchedFields);

                      // Validate form
                      const errors = await validateForm();

                      // If there are validation errors, don't save
                      if (Object.keys(errors).length > 0) {
                        toast.error(
                          "Please fix validation errors before saving as draft"
                        );
                        return;
                      }

                      // If validation passes, save as draft
                      handleSaveDraft(values as QuotationFormValues);
                    }}
                    onSave={async () => {
                      // Mark all fields as touched to show validation errors
                      const fields = Object.keys(values);
                      const touchedFields: any = {};
                      fields.forEach((field) => {
                        touchedFields[field] = true;
                      });
                      setTouched(touchedFields);

                      // Validate form
                      const errors = await validateForm();

                      // If there are validation errors, don't save
                      // if (Object.keys(errors).length > 0) {
                      //   toast.error(
                      //     "Please fix validation errors before saving the quotation"
                      //   );
                      //   return;
                      // }

                      // If validation passes, submit the form
                      handleSubmit();
                    }}
                    wrapperClassName="d-flex flex-wrap justify-content-center p-2 gap-md-3"
                    isSaving={isSaving}
                    isSavingDraft={isSavingDraft}
                  />
                </Col>
              </Row>
            </Form>
            </>
            </ScrollToFormikErrorWrapper>
          );
        }}
      </Formik>
    </PageContainer>
  );
};

export default ProductQuotationForm;

